import type { ReactElement, ReactNode } from 'react';
import { formGapSizes } from '~/components/layout/templates/FormLayout';
import Avatar from '~/components/new/Avatar.tsx';
import BigIcon, { BigIconName } from '~/components/new/BigIcon.tsx';
import Chip from '~/components/new/Chip';
import { Alert } from '@remotely/ui-kit';
import AvatarList from '~/components/ui/AvatarList.tsx';
import type { DataTestAttributes } from '~/utils/qa';
import { classNames } from '~/utils/styles.ts';

export default function CenteredInfoLayout({
    wide,
    verticallyAligned,
    children,
    className = '',
    gap = 'large',
    ['data-test-component']: testComponent = 'layout/templates/CenteredInfo',
}: {
    wide?: boolean;
    verticallyAligned?: boolean;
    children: ReactNode;
    className?: string;
    gap?: keyof typeof formGapSizes;
} & DataTestAttributes) {
    return (
        <div
            className={`layout layout-centered-info gap-${formGapSizes[gap]} ${classNames({
                'centered-layout': !!verticallyAligned,
                'max-w-8col': !!wide,
                'max-w-6col': !wide,
                [className]: Boolean(className),
            })}`}
            data-test-component={testComponent}>
            {children}
        </div>
    );
}

CenteredInfoLayout.Header = Header;

type HeaderProps = {
    title: ReactNode;
    subtitle?: ReactNode;
    alert?: string;
    tag?: ReactElement<typeof Chip>;
} & (
    | { icon: BigIconName; avatar?: undefined }
    | { icon?: undefined; avatar: ReactElement<typeof Avatar> | ReactElement<typeof AvatarList> }
) &
    DataTestAttributes;

function Header({
    title,
    subtitle,
    icon,
    avatar,
    alert,
    tag,
    ['data-test-name']: testName,
    ['data-test-component']: testComponent = 'layout/templates/CenteredInfo/Header',
}: HeaderProps) {
    return (
        <div
            className='layout-centered-info-header'
            data-test-component={testComponent}
            data-test-name={testName}>
            {icon && (
                <BigIcon
                    icon={icon}
                    size={120}
                    label='information'
                />
            )}
            {avatar && <div className='header-avatar'>{avatar}</div>}
            <div className='header-content'>
                <div className='header-content-text'>
                    <h1 className='header-content-text-title'>{title}</h1>
                    {subtitle && <p className='header-content-text-subtitle'>{subtitle}</p>}
                </div>
                {tag}
                {alert && <Alert variant='info'>{alert}</Alert>}
            </div>
        </div>
    );
}
CenteredInfoLayout.Placeholder = Placeholder;

type PlaceholderProps = {
    title?: string;
    icon?: BigIconName;
    subtitle?: ReactNode;
    children?: ReactNode;
    className?: string;
};

function Placeholder({
    title = 'No data available',
    className = '',
    subtitle,
    icon = 'emptyGeneric',
    children,
    ['data-test-component']: testComponent = 'layout/templates/CenteredInfo/Placeholder',
    ['data-test-name']: testName,
}: PlaceholderProps & DataTestAttributes) {
    return (
        <div
            className={classNames({
                'placeholder flex flex-column items-center gap-4': true,
                [className]: Boolean(className),
            })}
            data-test-component={testComponent}
            data-test-name={testName}>
            <div className='flex flex-column items-center gap-2'>
                <BigIcon
                    icon={icon}
                    size={80}
                    label={title}
                />
                <div className='flex flex-column gap-0'>
                    <span
                        className='text-center desktop-body-1-medium'
                        data-test-name='title'>
                        {title}
                    </span>
                    {subtitle && (
                        <span
                            className='text-center desktop-body-2-regular-lh-20 text-core-secondary'
                            data-test-name='subtitle'>
                            {subtitle}
                        </span>
                    )}
                </div>
            </div>
            {children}
        </div>
    );
}
